import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { compareRows, RevisionEntry } from './revisionModel';
import { revisionFactLabels, revisionFactText, revisionMakerText } from './revisionFactText';
import { revisionMessages } from './revisionMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RevisionDiffTableProps {
  a: RevisionEntry;
  b: RevisionEntry;
  currentUserId: number | null;
}

/** The facts of two revisions side by side (design d2). A row that differs is marked. */
const RevisionDiffTableComponent: React.FC<RevisionDiffTableProps> = ({ a, b, currentUserId }) => {
  const intl = useIntl();
  const rows = useMemo(() => compareRows(a, b), [a, b]);
  const makerA = revisionMakerText(intl, a, currentUserId);
  const makerB = revisionMakerText(intl, b, currentUserId);

  return (
    <div className={styles.diffGrid} role="table" data-testid="revision-diff">
      <span />
      <b>{intl.formatMessage(revisionMessages.number, { number: a.number })}</b>
      <b>{intl.formatMessage(revisionMessages.number, { number: b.number })}</b>
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          <span className={styles.diffKey}>{intl.formatMessage(revisionFactLabels[row.key])}</span>
          <span className={clsx(row.differs && styles.diffDiffers)}>
            {withBrandWord(revisionFactText(intl, row.key, row.a))}
          </span>
          <span className={clsx(row.differs && styles.diffDiffers)}>
            {withBrandWord(revisionFactText(intl, row.key, row.b))}
          </span>
        </React.Fragment>
      ))}
      <span className={styles.diffKey}>{intl.formatMessage(revisionMessages.factMadeBy)}</span>
      <span className={clsx(makerA !== makerB && styles.diffDiffers)}>{makerA}</span>
      <span className={clsx(makerA !== makerB && styles.diffDiffers)}>{makerB}</span>
    </div>
  );
};

export const RevisionDiffTable = TypedMemo(RevisionDiffTableComponent);
